import { Textarea } from '@/components/ui/textarea';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import BaseEvalConfig, {
  EvalFormValues,
  baseFormSchema,
} from './base-eval-config';
import { UseFormReturn } from 'react-hook-form';
import type { Eval } from '@/types/evals-types';
import { EvalModel } from '@/types/evals-types';
import { autoGenerateEvalConfig } from '@/services/evaluation/auto-generate-eval-config';
import { useNotification } from '@/components/notifications';
import { useEvalTypeMetadata } from '@/hooks/queries/evaluation/use-get-eval-types';
import { z } from '@/lib/zod-config';
import * as Sentry from '@sentry/react';
import HelpTooltip from '@/components/ui/help-tooltip';

interface PassFailEvalConfigProps {
  evalData: Eval;
  onSave: (data: Partial<Eval>) => void;
  onDuplicate: () => void;
  onDelete?: (evalId: string) => void;
}

// Form validation schema for pass/fail evaluation
const passFailEvalFormSchema = baseFormSchema.extend({
  eval_config: z.object({
    pass_criteria: z.string().trim().min(1, 'Pass criteria is required'),
    fail_criteria: z.string().trim(),
    na_criteria: z.string().trim(),
    additional_instructions: z.string().nullable().optional(),
  }),
});

export default function PassFailEvalConfig({
  evalData,
  onSave,
  onDuplicate,
  onDelete,
}: PassFailEvalConfigProps) {
  const notification = useNotification();
  const metadata = useEvalTypeMetadata('pass_fail');

  // Default values for the form
  const defaultValues: EvalFormValues = {
    name: evalData?.name || '',
    description: evalData?.description || '',
    model:
      evalData?.model === EvalModel.GPT_4O
        ? EvalModel.GPT_4O_MINI
        : evalData?.model || EvalModel.CLAUDE_SONNET_46,
    eval_config: {
      pass_criteria:
        evalData?.eval_config && 'pass_criteria' in evalData.eval_config
          ? evalData.eval_config.pass_criteria
          : '',
      fail_criteria:
        evalData?.eval_config && 'fail_criteria' in evalData.eval_config
          ? evalData.eval_config.fail_criteria
          : '',
      na_criteria:
        evalData?.eval_config && 'na_criteria' in evalData.eval_config
          ? evalData.eval_config.na_criteria
          : '',
      additional_instructions:
        evalData?.eval_config &&
        'additional_instructions' in evalData.eval_config
          ? (evalData.eval_config.additional_instructions ?? '')
          : '',
    },
  };

  // Handler for generating pass/fail/na criteria from description
  const handleGenerateFromDescription = async (
    form: UseFormReturn<EvalFormValues>,
  ) => {
    const description = form.getValues('description');
    const model = form.getValues('model');
    if (!description) {
      notification.error('Please provide a description first');
      return;
    }

    try {
      const response = await autoGenerateEvalConfig({
        model,
        eval_desc: description,
        na_allowed: true,
      });

      form.setValue('eval_config', {
        pass_criteria: response.pass_criteria,
        fail_criteria: response.fail_criteria,
        na_criteria: response.na_criteria,
      });
      // this triggers validation to update form state and display any validation errors.
      await form.trigger();
      notification.success('Evaluation criteria generated successfully');
    } catch (error) {
      console.error('Error generating criteria:', error);
      Sentry.captureException(error, {
        extra: {
          context: 'auto-generate-eval-config',
          model,
          descriptionLength: description.length,
        },
      });
      notification.error(
        'Auto-generation temporarily unavailable. Please configure manually.',
      );
    }
  };

  // Transform form data for saving
  const onSubmitTransform = (data: EvalFormValues): Partial<Eval> => ({
    ...data,
    id: evalData.id,
    type: 'pass_fail',
    eval_config: {
      ...data.eval_config,
      additional_instructions:
        data.eval_config && 'additional_instructions' in data.eval_config
          ? data.eval_config.additional_instructions === ''
            ? null
            : data.eval_config.additional_instructions
          : undefined,
    },
  });

  // Render type-specific fields
  const renderFields = (
    form: UseFormReturn<EvalFormValues>,
    isEditing: boolean,
  ) => (
    <>
      <FormField
        control={form.control}
        name='eval_config.pass_criteria'
        render={({ field }) => (
          <FormItem>
            <div className='flex items-center gap-2'>
              <FormLabel className='label-required'>Pass Criteria</FormLabel>
              <HelpTooltip label='Pass criteria help'>
                Describe what success looks like for this evaluation.
              </HelpTooltip>
            </div>

            <FormControl>
              <Textarea
                placeholder='e.g. pass if the approach is highly original and leaves a strong impact'
                className='min-h-[100px]'
                disabled={!isEditing}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name='eval_config.fail_criteria'
        render={({ field }) => (
          <FormItem>
            <div className='flex items-center gap-2'>
              <FormLabel>Fail Criteria (Optional)</FormLabel>
              <HelpTooltip label='Fail criteria help'>
                Describe what failure looks like for this evaluation.
              </HelpTooltip>
            </div>
            <FormControl>
              <Textarea
                placeholder='e.g. fail if the approach is unoriginal and offers no unique perspective'
                className='min-h-[100px]'
                disabled={!isEditing}
                {...field}
              />
            </FormControl>
            <FormMessage />
            <FormDescription className='text-amber-600 dark:text-amber-500'>
              {!field.value?.trim()
                ? 'Without failing criteria, the judge model may default to PASS for all inputs.'
                : ''}
            </FormDescription>
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name='eval_config.na_criteria'
        render={({ field }) => (
          <FormItem>
            <div className='flex items-center gap-2'>
              <FormLabel>N/A Criteria (Optional)</FormLabel>
              <HelpTooltip label='N/A criteria help'>
                Use this to explain when neither pass nor fail is appropriate.
              </HelpTooltip>
            </div>
            <FormControl>
              <Textarea
                placeholder='e.g. use when only factual or verbatim answers apply'
                className='min-h-[100px]'
                disabled={!isEditing}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name='eval_config.additional_instructions'
        render={({ field }) => (
          <FormItem>
            <div className='flex items-center gap-2'>
              <FormLabel>Additional Instructions</FormLabel>
              <HelpTooltip label='Additional instructions help'>
                Provide further details or notes to guide the evaluator.
              </HelpTooltip>
            </div>
            <FormControl>
              <Textarea
                className='min-h-[100px]'
                disabled={!isEditing}
                {...field}
                value={field.value ?? ''}
                onChange={(e) => field.onChange(e.target.value)}
                placeholder='e.g. include judging notes, edge cases, or style preferences'
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );

  return (
    <BaseEvalConfig
      evalData={evalData}
      onSave={onSave}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
      defaultValues={defaultValues}
      onGenerateFromDescription={handleGenerateFromDescription}
      title='Pass/Fail Evaluation'
      description={metadata?.description}
      onSubmitTransform={onSubmitTransform}
      validationSchema={passFailEvalFormSchema}
      metadata={metadata}
    >
      {renderFields}
    </BaseEvalConfig>
  );
}
